import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Award, Zap, Eye, ShieldCheck, PhoneCall, Activity, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import ServiceCarousel from '../components/ServiceCarousel';
import { services, whyChooseUs, processSteps, results } from '../data/mock';
import { trackConsultationClick } from '../utils/analytics';

const iconMap = {
  'trending-up': TrendingUp,
  'users': Users,
  'award': Award,
  'zap': Zap,
  'eye': Eye,
  'shield-check': ShieldCheck,
  'phone-call': PhoneCall,
  'activity': Activity
};

const Home = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    const elements = document.querySelectorAll('.animate-on-scroll');
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, { threshold: 0.1 });

    observerRef.current = observer;
    elements.forEach((el) => observer.observe(el));

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []); // No external dependencies needed - DOM query and observer setup

  const handleConsultation = () => {
    trackConsultationClick('hero_section');
    window.open('https://wa.me/918185882299?text=Hi! I would like to get a free consultation.', '_blank');
  };

  return (
    <div className="min-h-screen bg-black overflow-hidden">
      {/* Premium Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center gradient-animated pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-32 text-center">
          <div className="space-y-8">
            {/* Heading - Stagger 1 */}
            <h1 className="animate-fade-in-up stagger-1 text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight">
              <span className="text-white">AI-Powered Growth</span>
              <br />
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                Engineered for Dominance
              </span>
            </h1>
            
            {/* Subtext - Stagger 2 */}
            <p className="animate-fade-in-up stagger-2 text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed font-light">
              Premium digital systems combining Google Ads, Meta Ads, and AI strategy to generate consistent leads and revenue.
            </p>
            
            {/* Buttons - Stagger 3 */}
            <div className="animate-fade-in-up stagger-3 flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <Button
                onClick={handleConsultation}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-10 py-6 text-lg rounded-full font-medium shadow-2xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-500 hover:scale-105"
              >
                Get Free Consultation <ArrowRight className="ml-2" size={20} />
              </Button>
              <Link to="/services">
                <Button
                  variant="outline"
                  className="border-2 border-cyan-500/30 text-white hover:bg-cyan-500/10 hover:border-cyan-400 px-10 py-6 text-lg rounded-full font-medium transition-all duration-500 hover:scale-105"
                >
                  Explore Services
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator - Visible and Animated */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-50">
          <div className="scroll-indicator">
            <div className="w-8 h-14 border-2 rounded-full flex justify-center items-start pt-3 scroll-indicator-border bg-black/20 backdrop-blur-sm">
              <div className="w-2 h-5 rounded-full scroll-indicator-wheel shadow-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Carousel Section */}
      <section className="section-spacing relative">
        <div className="gradient-bg">
          <div className="text-center mb-20 animate-on-scroll">
            <div className="inline-block mb-4">
              <span className="text-indigo-400 text-sm font-semibold tracking-wider uppercase">Our Services</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Complete Growth <span className="text-indigo-400">Solutions</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">
              Comprehensive services designed to drive measurable results
            </p>
          </div>
          <ServiceCarousel />
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-spacing px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card
                key={service.id}
                className="glass-premium premium-card p-8 rounded-3xl group animate-on-scroll"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 flex items-center justify-center group-hover:bg-indigo-500/20 transition-colors duration-300">
                    <img src={service.image} alt={service.title} className="w-8 h-8 object-cover rounded-lg opacity-80" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-indigo-400 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm font-light">
                  {service.shortDesc}
                </p>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-16 animate-on-scroll">
            <Link to="/services">
              <Button className="bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-4 rounded-full font-medium shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 transition-all duration-300 hover:scale-105">
                View All Services <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-spacing px-6 lg:px-8 gradient-bg">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 animate-on-scroll">
            <div className="inline-block mb-4">
              <span className="text-indigo-400 text-sm font-semibold tracking-wider uppercase">Why Choose Us</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Built for <span className="text-indigo-400">Excellence</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <Card
                  key={item.id}
                  className="glass-premium premium-card p-8 rounded-3xl text-center group animate-on-scroll"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center group-hover:bg-indigo-500/20 transition-all duration-300">
                      {IconComponent && <IconComponent size={28} className="text-indigo-400" />}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 text-sm font-light">
                    {item.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-spacing px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 animate-on-scroll">
            <div className="inline-block mb-4">
              <span className="text-indigo-400 text-sm font-semibold tracking-wider uppercase">Our Process</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Proven <span className="text-indigo-400">Methodology</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <div
                key={step.id}
                className="relative animate-on-scroll"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <Card className="glass-premium premium-card p-8 rounded-3xl group h-full">
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-indigo-500/50">
                    {step.id}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3 mt-4 group-hover:text-indigo-400 transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm font-light leading-relaxed">
                    {step.description}
                  </p>
                </Card>
                
                {/* Connector Line */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-indigo-500/50 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="section-spacing px-6 lg:px-8 gradient-bg">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 animate-on-scroll">
            <div className="inline-block mb-4">
              <span className="text-indigo-400 text-sm font-semibold tracking-wider uppercase">Impact</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Measurable <span className="text-indigo-400">Results</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {results.map((result, index) => {
              const IconComponent = iconMap[result.icon];
              return (
                <Card
                  key={result.id}
                  className="glass-premium premium-card p-8 rounded-3xl text-center group animate-on-scroll"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center group-hover:bg-indigo-500/20 transition-all duration-300">
                      {IconComponent && <IconComponent size={28} className="text-indigo-400" />}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {result.title}
                  </h3>
                  <p className="text-gray-400 text-sm font-light">
                    {result.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-spacing px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Card className="glass-premium glow-subtle p-16 rounded-3xl text-center animate-on-scroll">
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Ready to <span className="text-indigo-400">Transform</span>?
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto font-light">
              Let's build a growth system that generates consistent leads and revenue
            </p>
            <Button
              onClick={handleConsultation}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-12 py-6 text-lg rounded-full font-medium shadow-2xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-500 hover:scale-105"
            >
              Get Free Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;
